import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Seo } from "@/components/Seo";
import { FadeUp } from "@/components/anim";
import { api } from "@/lib/api";
import { fmtDate, FALLBACK_IMAGE, SITE_URL } from "@/lib/site";

const ProgressBar = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setProgress(total > 0 ? (h.scrollTop / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-1 bg-transparent" aria-hidden="true">
      <div className="h-full bg-[hsl(var(--kerrys-coral))] transition-[width] duration-100" style={{ width: `${progress}%` }} />
    </div>
  );
};

const renderContent = (content) =>
  (content || "").split("\n\n").map((block, i) => {
    if (block.startsWith("## ")) {
      return <h2 key={i}>{block.replace(/^## /, "")}</h2>;
    }
    return (
      <p key={i}>
        {block.split("\n").map((line, j, arr) => (
          <span key={j}>
            {line}
            {j < arr.length - 1 && <br />}
          </span>
        ))}
      </p>
    );
  });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
    api
      .get("/blog")
      .then((res) => setRelated(res.data))
      .catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="py-32 text-center px-4" data-testid="blog-post-not-found">
        <p className="font-display uppercase text-7xl text-[hsl(var(--kerrys-coral))]">Hmm.</p>
        <h1 className="mt-4 text-2xl font-bold">We couldn&rsquo;t find that post</h1>
        <Button asChild className="mt-8 rounded-none bg-foreground text-background hover:bg-foreground/90 font-bold uppercase tracking-[0.12em] text-xs h-11 px-6">
          <Link to="/blog">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to the blog
          </Link>
        </Button>
      </div>
    );
  }

  const relatedPosts = post ? related.filter((p) => p.slug !== post.slug).slice(0, 3) : [];

  const jsonLd = post
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.excerpt,
        image: post.image_url,
        datePublished: post.created_at,
        dateModified: post.updated_at,
        author: { "@type": "Person", name: post.author || "Kerry White" },
        publisher: { "@type": "Organization", name: "Kerry's" },
        mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
      }
    : null;

  return (
    <>
      {post && (
        <Seo title={post.title} description={post.excerpt} path={`/blog/${post.slug}`} image={post.image_url} type="article" jsonLd={jsonLd} />
      )}
      <ProgressBar />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        {!post ? (
          <div className="space-y-6">
            <Skeleton className="h-8 w-40 rounded-none" />
            <Skeleton className="h-16 w-full rounded-none" />
            <Skeleton className="h-72 w-full rounded-none" />
            <Skeleton className="h-40 w-full rounded-none" />
          </div>
        ) : (
          <>
            <Breadcrumb className="mb-10">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/blog">Blog</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="line-clamp-1">{post.title}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <FadeUp>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[hsl(var(--kerrys-sea))]">
                {post.category} &middot; {fmtDate(post.created_at)}
              </p>
              <h1 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mt-5" data-testid="blog-post-title">
                {post.title}
              </h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{post.excerpt}</p>
            </FadeUp>
            {post.image_url && (
              <FadeUp delay={0.08}>
                <div className="mt-10 border-[0.5px] border-foreground">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-64 sm:h-96 object-cover"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_IMAGE;
                    }}
                  />
                </div>
              </FadeUp>
            )}
            <div className="blog-prose mt-12" data-testid="blog-post-content">
              {renderContent(post.content)}
            </div>
            <div className="border-[0.5px] border-foreground bg-secondary grain mt-14 p-7 flex items-center gap-5">
              <span className="font-display text-3xl h-14 w-14 bg-foreground text-background flex items-center justify-center shrink-0">
                K
              </span>
              <div>
                <p className="font-bold">{post.author || "Kerry White"}</p>
                <p className="text-sm text-muted-foreground">Principal of Kerry&rsquo;s &middot; Whitstable</p>
              </div>
            </div>
          </>
        )}
      </article>

      {post && relatedPosts.length > 0 && (
        <section className="bg-secondary grain border-t-[0.5px] border-foreground/40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-display uppercase text-4xl sm:text-5xl leading-none">More from the studio</h2>
              <Button
                asChild
                variant="outline"
                className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-6 hidden sm:inline-flex"
              >
                <Link to="/blog" data-testid="blog-post-all-posts-button">
                  All posts <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((p, i) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className={`group block border-[0.5px] border-foreground bg-card ${i === 1 ? "md:mt-10" : ""}`}
                  data-testid="blog-related-card"
                >
                  <div className="overflow-hidden border-b-[0.5px] border-foreground">
                    <img
                      src={p.image_url || FALLBACK_IMAGE}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-40 object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[hsl(var(--kerrys-sea))]">{p.category}</p>
                    <h3 className="font-display uppercase text-2xl leading-[0.95] mt-2.5 group-hover:text-[hsl(var(--kerrys-coral))] transition-colors line-clamp-2">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
